import React, {useEffect, useState} from 'react';
import SuccessorTableRow from './SuccessorTableRow';
import {
    EstateMember,
    FamilyMembers,
    FiduciaryMember,
    FiduciaryMemberType,
    Member,
    MemberType,
    Trustee,
} from "../../model/EstateFlowchartTypes";
import {SubSectionHeader} from 'src/Assets/Ownership/components/SubSectionHeader';
import {isCustomMember, isUUID, parseFamilyMembers} from '../../utils/DefUtils';
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd';
import {familyMembersSelector} from "../../slices/EstateFlowChartSlice";
import "src/EstateFlowchart/components/SuccessorTable/_successor-table.scss"
import {useAppSelector} from "../../../store/hooks";
import {selectReleaseToggles} from "src/ReleaseToggles/releaseTogglesSlice";

type SuccessorTableProps = {
    readonly dropdownHeight?: number,
    readonly formData: any,
    readonly successorType: MemberType | FiduciaryMemberType,
    readonly updateData: Function,
    readonly title?: string,
    readonly buttonText?: string,
    readonly rowName?: string,
    readonly customKey?: string
    readonly includeNT?: boolean,
    readonly showSubtitle?: boolean
}

export type SuccessorRow = {
    order: number,
    type?: string,
    name: string,
    successors: Member[],
    selectedSuccessors: string[],
}
/**
 * SuccessorTable component renders a table of successor trustees with drag-and-drop functionality.
 * It allows adding, deleting, and reordering successor trustees, and updates the form data accordingly.
 *
 * @param {object} props - The properties object.
 * @param {number} props.dropdownHeight - The height of the dropdown menu.
 * @param {FormData} props.formData - The form data containing the current state of the form.
 * @param {Function} props.updateData - State update hook for the form data.
 * @param {boolean} props.includeNT - Flag to include "Northern Trust" as an option. Default True
 *
 * @returns {JSX.Element} The rendered SuccessorTable component.
 */
function SuccessorTable({
                            dropdownHeight = 0,
                            formData,
                            successorType,
                            updateData,
                            title,
                            buttonText,
                            rowName,
                            customKey = 'successors',
                            includeNT = true,
                            showSubtitle = false,
                        }: SuccessorTableProps): JSX.Element {
    const [successorRows, setSuccessorRows] = useState<SuccessorRow[]>([]);
    const [successors, setSuccessors] = useState<Member[]>([]);
    const rawFamilyMembers = useAppSelector(familyMembersSelector);
    const [parsedFamilyMembers, setParsedFamilyMembers] = useState<FamilyMembers | undefined>(undefined);
    const [lastRowIndex, setLastRowIndex] = useState<number | null>(null);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        if (releaseToggles?.enableEstateFlowchartMVPRelease && successorRows.length > 0 && lastRowIndex !== null) {
            const successorRow = successorRows[lastRowIndex ?? 0];

            const rowElement = document.getElementById(`successor-row-${successorRow?.name}-${successorRow.type}-${successorRow?.order}`);
            if (rowElement) {
                rowElement.scrollIntoView({behavior: "smooth", block: "center"});
                setLastRowIndex(null);
            }
        }
    }, [successorRows, lastRowIndex]);

    const createEstateMember = (element: Member, order: number): EstateMember | null => {
        let member: EstateMember = {
            familyMember: !isCustomMember(element.value, parsedFamilyMembers ?? {} as FamilyMembers),
            customName: formData?.[customKey]?.find((item: EstateMember) => item.memberId === element.value)?.customName ?? "",
            memberOrder: order,
            createdDate: formData?.[customKey]?.find((item: EstateMember) => item.memberId === element.value)?.createdDate ?? undefined,
            id: formData?.[customKey]?.find((item: EstateMember) => item.memberId === element.value)?.id ?? undefined, // member order before the change combined with the memberId creates a compound key for id
        };
        if (typeof successorType === 'string' && Object.values(MemberType).includes(successorType as MemberType)) {
            member = {
                ...member,
                trustId: formData ? formData.revTrustId : "",
                memberType: successorType,
            } as Trustee
        } else if (typeof successorType === 'string' && Object.values(FiduciaryMemberType).includes(successorType as FiduciaryMemberType)) {
            member = {
                ...member,
                fiduciaryAssignmentId: formData ? formData.fiduciaryAssignmentId : "",
                fiduciaryMemberType: successorType
            } as FiduciaryMember
        }
        if (element.selected) {
            let item = member
            item.customName = isCustomMember(element.value, parsedFamilyMembers ?? {} as FamilyMembers) ? element.label : "";
            item.familyMember = !isCustomMember(element.value, parsedFamilyMembers ?? {} as FamilyMembers);
            item.memberId = isUUID(element.value) ? element.value : undefined;
            return member
        }
        return null
    };

    const onChangeSuccessor = (selectedOptions: Member[], index: number) => {
        const updatedRows = successorRows.map(row =>
            row.order === index ? {...row, selectedSuccessors: selectedOptions.map(option => option.value)} : row
        );

        setSuccessorRows(updatedRows);

        const updatedMembers = selectedOptions.map(option =>
            createEstateMember(option, index)
        ).filter(Boolean) as EstateMember[];
        const memberList = formData?.[customKey] ?? [];
        const remainingMembers: EstateMember[] = memberList.filter((member: EstateMember) => member.memberOrder !== index);
        updateData({
            ...formData,
            [customKey]: [...remainingMembers, ...updatedMembers]
        });
    };

    const onDeleteSuccessor = (order: number) => {
        successorRows.sort((a => a.order));
        const updatedRows = successorRows.filter(row => row.order.toString() !== order.toString());
        setSuccessorRows(updatedRows);

        const remainingMembers: EstateMember[] = formData?.[customKey].filter((member: EstateMember) => member.memberOrder !== order);
        updateData({...formData, [customKey]: remainingMembers});
    }

    const buildInitialSuccessorRows = (familyMembers: Member[] | undefined) => {
        if (familyMembers && formData?.[customKey] && formData?.[customKey].length > 0) {
            const successorsFromState = [...(formData?.[customKey] ?? [])].sort((a, b) => a.memberOrder && b.memberOrder ? a.memberOrder - b.memberOrder : 0);
            const successorsByOrder: any = {};
            successorsFromState?.forEach((item: Trustee) => {
                if (item.memberOrder === undefined) return;
                successorsByOrder[item.memberOrder] ??= [];
                successorsByOrder[item.memberOrder].push(item);
            })
            for (const i in successorsByOrder) {
                let row: SuccessorRow = {
                    name: "successor",
                    selectedSuccessors: [],
                    successors: familyMembers.slice(),
                    order: Number(i)
                };
                row.selectedSuccessors = successorsByOrder[i].map((member: Trustee) => member.memberId);
                successorsByOrder[i].forEach((member: Trustee) => {
                    if (!member.familyMember) {
                        let item = {label: "", selected: false, value: "", icon: ""};
                        item.selected = true;
                        item.label = member.customName ?? "";
                        item.value = member.memberId ?? "";
                        row.successors.push(item);
                    }
                })
                setSuccessorRows(prevArray => [...prevArray, row]);
            }
        }
    }

    const onAddSuccessor = () => {
        let newSuccessor = {
            name: "successor",
            type: successorType.toLowerCase(),
            selectedSuccessors: [],
            successors: [...successors],
            order: successorRows[successorRows.length - 1]?.order + 1 || 0
        };
        setSuccessorRows(prevRows => {
            const updatedRows = [...prevRows, newSuccessor];
            setLastRowIndex(updatedRows.length - 1);
            return updatedRows;
        });
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        const rows = Array.from(successorRows);
        const [reorderedRow] = rows.splice(result.source.index, 1);
        rows.splice(result.destination.index, 0, reorderedRow);
        rows.forEach((item, index) => item.order = index);
        let members: (EstateMember | null)[] = []
        rows.forEach(row => {
            // Get selected members from the row.successors list
            const selectedSuccessorMembers: Member[] = row.successors.filter((member: Member) => row.selectedSuccessors.includes(member.value));

            // Add any custom members that were selected but are not already in row.successors
            row.selectedSuccessors.forEach(selectedValue => {
                let exists = false;
                for (let i = 0; i < selectedSuccessorMembers.length; i++) {
                    if (selectedSuccessorMembers[i].value === selectedValue) {
                        exists = true;
                        break;
                    }
                }
                if (!exists) {
                    // Create a custom member object
                    selectedSuccessorMembers.push({
                        value: selectedValue,
                        label: selectedValue,
                        selected: true,
                    });
                    row.successors.push({
                        value: selectedValue,
                        label: selectedValue,
                        selected: true,
                    });
                }
            });

            selectedSuccessorMembers.forEach(member => {
                member.selected = true;
                members.push(createEstateMember(member, row.order));
                member.selected = false;
            });
        });
        setSuccessorRows(rows);
        const sortedMembers = (members.filter(Boolean) as EstateMember[]).sort((a, b) => (a.memberOrder ?? 0) - (b.memberOrder ?? 0));

        updateData({...formData, [customKey]: sortedMembers});
    }

    useEffect(() => {
        if (rawFamilyMembers && successorRows.length === 0) {
            const parsedFamilyMembers: FamilyMembers = parseFamilyMembers(rawFamilyMembers);
            setParsedFamilyMembers(parsedFamilyMembers);
            if (parsedFamilyMembers) {
                buildInitialSuccessorRows([
                    ...((parsedFamilyMembers && parsedFamilyMembers.familyMember) ? parsedFamilyMembers.familyMember : []),
                    {
                        value: parsedFamilyMembers?.value ?? "",
                        label: parsedFamilyMembers?.label ?? "",
                        selected: false
                    }
                ]);
                if (successors.length === 0) {
                    setSuccessors(prevArray => [
                        ...prevArray,
                        ...(parsedFamilyMembers?.familyMember ?? []),
                        {
                            value: parsedFamilyMembers?.value,
                            label: parsedFamilyMembers?.label,
                            selected: false
                        }
                    ]);
                }
            }
        }
    }, [rawFamilyMembers, formData?.[customKey]]);

    const subtitleText = "Select from dropdown or type to add new name if not listed. If multiple trustees are serving together, add them in the same field. Reorder succession using the drag icon.";
    return (
        <div className="layout-data-entry-form__field successor-table" data-testid={`${title}-SuccessorTable`}>
            <DragDropContext onDragEnd={onDragEnd}>
                <SubSectionHeader
                    onAddButtonClick={onAddSuccessor}
                    title={title ?? "Successors"}
                    buttonText={buttonText ?? "ADD SUCCESSOR TRUSTEE"}
                    subtitle={showSubtitle && successorRows.length ? subtitleText : undefined}
                />
                {
                    successorRows.length > 0 ? (
                        <div className="successor-table-header-grid" data-testid="successor-table-header">
                            <span id="successor-order">ORDER</span> <span
                            id="successor-name">{rowName ?? "SUCCESSOR NAME"}</span>
                        </div>
                    ) : null
                }
                <Droppable droppableId="successor-table">
                    {(provided) => (
                        <div className="successor-input-grid"
                             ref={provided.innerRef}>
                            {successorRows.map((row, index) => {
                                return (
                                    <Draggable key={row.order} draggableId={row.order.toString()} index={index}>
                                        {(p) => (
                                            <SuccessorTableRow row={row}
                                                               id={`successor-row-${row.name}-${row.type}-${row.order}`}
                                                               key={`successor-row-${row.name}-${row.type}-${row.order}`}
                                                               index={row.order}
                                                               includeNT={includeNT}
                                                               dropdownHeight={dropdownHeight}
                                                               onChangeSuccessor={onChangeSuccessor}
                                                               onDeleteSuccessor={onDeleteSuccessor} provided={p}/>
                                        )}
                                    </Draggable>
                                )
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>

    )
}

export default SuccessorTable